import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type Currency = "EUR" | "USD" | "GBP" | "TRY";

export interface ActivityPrices {
  EUR?: number;
  USD?: number;
  GBP?: number;
  TRY?: number;
}

interface ExchangeRate {
  target_currency: string;
  rate: number;
}

interface CurrencyContextType {
  currency: Currency;
  setCurrency: (currency: Currency) => void;
  formatPrice: (priceInEur: number, activityPrices?: ActivityPrices) => string;
  convertPrice: (priceInEur: number, targetCurrency?: Currency) => number;
  exchangeRates: Record<Currency, number>;
  currencySymbols: Record<Currency, string>;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

const CURRENCY_STORAGE_KEY = "goexcurs_currency";

const currencySymbols: Record<Currency, string> = {
  EUR: "€",
  USD: "$",
  GBP: "£",
  TRY: "₺",
};

// Default exchange rates as fallback
const defaultRates: Record<Currency, number> = {
  EUR: 1,
  USD: 1.08,
  GBP: 0.86,
  TRY: 35.50,
};

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [currency, setCurrencyState] = useState<Currency>(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem(CURRENCY_STORAGE_KEY);
      if (saved && ["EUR", "USD", "GBP", "TRY"].includes(saved)) {
        return saved as Currency;
      }
    }
    return "EUR";
  });

  // Fetch exchange rates from database
  const { data: exchangeRatesData } = useQuery({
    queryKey: ["exchange-rates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("currency_exchange_rates")
        .select("target_currency, rate")
        .eq("base_currency", "EUR");
      
      if (error) {
        console.error("Error fetching exchange rates:", error);
        return null;
      }
      return data as ExchangeRate[];
    },
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
  });

  // Build exchange rates map
  const exchangeRates: Record<Currency, number> = Array.isArray(exchangeRatesData)
    ? exchangeRatesData.reduce((acc, rate) => {
        acc[rate.target_currency as Currency] = Number(rate.rate);
        return acc;
      }, { ...defaultRates })
    : defaultRates;

  useEffect(() => {
    localStorage.setItem(CURRENCY_STORAGE_KEY, currency);
  }, [currency]);

  const setCurrency = (newCurrency: Currency) => {
    setCurrencyState(newCurrency);
  };

  // Convert price from EUR to target currency
  const convertPrice = (priceInEur: number, targetCurrency?: Currency): number => {
    const target = targetCurrency || currency;
    const rate = exchangeRates[target] || 1;
    return Math.round(priceInEur * rate * 100) / 100;
  };

  // Format price with currency symbol
  // If activityPrices is provided, use the specific price for the currency if available
  const formatPrice = (priceInEur: number, activityPrices?: ActivityPrices): string => {
    let displayPrice: number;
    
    // Check if we have a specific price for this currency
    if (activityPrices && activityPrices[currency] !== undefined) {
      displayPrice = activityPrices[currency]!;
    } else {
      // Convert from EUR
      displayPrice = convertPrice(priceInEur);
    }

    // Format the number based on currency
    const symbol = currencySymbols[currency];
    
    // For TRY, don't show decimals if it's a whole number or round to whole numbers for cleaner display
    if (currency === "TRY") {
      return `${symbol}${Math.round(displayPrice).toLocaleString()}`;
    }
    
    // For other currencies, show 2 decimal places only if needed
    if (displayPrice % 1 === 0) {
      return `${symbol}${displayPrice.toLocaleString()}`;
    }
    return `${symbol}${displayPrice.toFixed(2)}`;
  };

  return (
    <CurrencyContext.Provider value={{ 
      currency, 
      setCurrency, 
      formatPrice, 
      convertPrice,
      exchangeRates,
      currencySymbols
    }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error("useCurrency must be used within a CurrencyProvider");
  }
  return context;
}

// Custom hook to fetch activity-specific prices
export function useActivityPrices(activityId: string | undefined) {
  return useQuery({
    queryKey: ["activity-prices", activityId],
    queryFn: async () => {
      if (!activityId) return null;
      
      const { data, error } = await supabase
        .from("activity_prices")
        .select("currency, adult_price, child_price, infant_price, is_manual_override")
        .eq("activity_id", activityId);
      
      if (error) {
        console.error("Error fetching activity prices:", error);
        return null;
      }
      
      // Convert to a map of currency -> prices
      const pricesMap: Record<Currency, { 
        adult: number; 
        child?: number; 
        infant?: number;
        isManual: boolean;
      }> = {} as any;
      
      data?.forEach((p) => {
        pricesMap[p.currency as Currency] = {
          adult: Number(p.adult_price),
          child: p.child_price ? Number(p.child_price) : undefined,
          infant: p.infant_price ? Number(p.infant_price) : undefined,
          isManual: p.is_manual_override,
        };
      });
      
      return pricesMap;
    },
    enabled: !!activityId,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
}
